import { PayloadAction } from "@reduxjs/toolkit";

import { AutocompleteItem } from "../../../../components/Autocomplete";
import { FormId } from "../../../../components/enums";
import { defaultCountry } from "../../../../shared";
import {
  createGenericUISlice,
  GenericState,
  UIState,
} from "../../../../store/HOC/genericUI.slice";

import { ComponentId as CompanyComponentId } from "./CompanyData/enums";
import { ComponentId as ContactPersonComponentId } from "./ContactPersonData/enums";
import { ComponentId as GeneralComponentId } from "./GeneralData/enums";
import { ComponentId as PersonComponentId } from "./PersonData/enums";

export type FormDataInputs = {
  [FormId.OWNER_GENERAL]: {
    [GeneralComponentId.TYPE]: string;
  };
  [FormId.OWNER_PERSON_DATA]: {
    [PersonComponentId.NAME]: string;
    [PersonComponentId.SURNAME]: string;
    [PersonComponentId.PESEL]: string;
    [PersonComponentId.PHONE]: string;
    [PersonComponentId.EMAIL]: string;
    [PersonComponentId.STREET]: string;
    [PersonComponentId.STREET_2]: string;
    [PersonComponentId.ZIP_CODE_PL]: string;
    [PersonComponentId.ZIP_CODE_GENERIC]: string;
    [PersonComponentId.CITY]: string;
    [PersonComponentId.COUNTRY]: AutocompleteItem;
    [PersonComponentId.PROVINCE]: string;
  };
  [FormId.OWNER_COMPANY_DATA]: {
    [CompanyComponentId.NAME]: string;
    [CompanyComponentId.REGON]: string;
    [CompanyComponentId.PHONE]: string;
    [CompanyComponentId.EMAIL]: string;
    [CompanyComponentId.STREET]: string;
    [CompanyComponentId.STREET_2]: string;
    [CompanyComponentId.ZIP_CODE_PL]: string;
    [CompanyComponentId.ZIP_CODE_GENERIC]: string;
    [CompanyComponentId.CITY]: string;
    [CompanyComponentId.COUNTRY]: AutocompleteItem;
    [CompanyComponentId.PROVINCE]: string;
  };
  [FormId.OWNER_CONTACT_PERSON_DATA]: {
    [ContactPersonComponentId.TYPE]: string;
    [ContactPersonComponentId.NAME]: string;
    [ContactPersonComponentId.SURNAME]: string;
    [ContactPersonComponentId.COMPANY_NAME]: string;
    [ContactPersonComponentId.PHONE]: string;
    [ContactPersonComponentId.EMAIL]: string;
  };
};

export type OwnerGeneralFormInputs = FormDataInputs[FormId.OWNER_GENERAL];
export type OwnerPersonFormInputs = FormDataInputs[FormId.OWNER_PERSON_DATA];
export type OwnerCompanyFormInputs = FormDataInputs[FormId.OWNER_COMPANY_DATA];
export type OwnerContactPersonFormInputs = FormDataInputs[FormId.OWNER_CONTACT_PERSON_DATA];

type OwnerState = {
  data: FormDataInputs;
  ui: UIState;
};

const initialState: OwnerState = {
  data: {
    [FormId.OWNER_GENERAL]: {
      [GeneralComponentId.TYPE]: "",
    },
    [FormId.OWNER_PERSON_DATA]: {
      [PersonComponentId.NAME]: "",
      [PersonComponentId.SURNAME]: "",
      [PersonComponentId.PESEL]: "",
      [PersonComponentId.PHONE]: "",
      [PersonComponentId.EMAIL]: "",
      [PersonComponentId.STREET]: "",
      [PersonComponentId.STREET_2]: "",
      [PersonComponentId.ZIP_CODE_PL]: "",
      [PersonComponentId.ZIP_CODE_GENERIC]: "",
      [PersonComponentId.CITY]: "",
      [PersonComponentId.COUNTRY]: {
        ...defaultCountry,
      },
      [PersonComponentId.PROVINCE]: "",
    },
    [FormId.OWNER_COMPANY_DATA]: {
      [CompanyComponentId.NAME]: "",
      [CompanyComponentId.REGON]: "",
      [CompanyComponentId.PHONE]: "",
      [CompanyComponentId.EMAIL]: "",
      [CompanyComponentId.STREET]: "",
      [CompanyComponentId.STREET_2]: "",
      [CompanyComponentId.ZIP_CODE_PL]: "",
      [CompanyComponentId.ZIP_CODE_GENERIC]: "",
      [CompanyComponentId.CITY]: "",
      [CompanyComponentId.COUNTRY]: {
        ...defaultCountry,
      },
      [CompanyComponentId.PROVINCE]: "",
    },
    [FormId.OWNER_CONTACT_PERSON_DATA]: {
      [ContactPersonComponentId.TYPE]: "",
      [ContactPersonComponentId.NAME]: "",
      [ContactPersonComponentId.SURNAME]: "",
      [ContactPersonComponentId.COMPANY_NAME]: "",
      [ContactPersonComponentId.PHONE]: "",
      [ContactPersonComponentId.EMAIL]: "",
    },
  },
  ui: {
    validated: false,
    "person[country]": defaultCountry.value,
    "company[country]": defaultCountry.value,
  },
};

const ownerSlice = createGenericUISlice({
  name: "@OC/Property/owner",
  initialState: initialState as GenericState<FormDataInputs>,
  reducers: {
    setData: (state, action: PayloadAction<FormDataInputs>) => {
      state.data[FormId.OWNER_GENERAL] = {
        ...initialState.data[FormId.OWNER_GENERAL],
        ...action.payload[FormId.OWNER_GENERAL],
      };
      state.data[FormId.OWNER_PERSON_DATA] = {
        ...initialState.data[FormId.OWNER_PERSON_DATA],
        ...action.payload[FormId.OWNER_PERSON_DATA],
      };
      state.data[FormId.OWNER_COMPANY_DATA] = {
        ...initialState.data[FormId.OWNER_COMPANY_DATA],
        ...action.payload[FormId.OWNER_COMPANY_DATA],
      };
      state.data[FormId.OWNER_CONTACT_PERSON_DATA] = {
        ...initialState.data[FormId.OWNER_CONTACT_PERSON_DATA],
        ...action.payload[FormId.OWNER_CONTACT_PERSON_DATA],
      };
      state.ui.validated = true;
    },
    setAvailableCities: (
      state,
      action: PayloadAction<{ label: string; value: string }[]>
    ) => {
      state.ui["ownerAvailableCities"] = action.payload;
    },
    setOwnerType: (state, action: PayloadAction<string>) => {
      state.data[FormId.OWNER_GENERAL][GeneralComponentId.TYPE] =
        action.payload;
      state.ui["person[country]"] =
        state.data[FormId.OWNER_PERSON_DATA][PersonComponentId.COUNTRY].value;
      state.ui["company[country]"] =
        state.data[FormId.OWNER_COMPANY_DATA][CompanyComponentId.COUNTRY].value;
    },
    setContactPersonType: (state, action: PayloadAction<string>) => {
      state.data[FormId.OWNER_CONTACT_PERSON_DATA][
        ContactPersonComponentId.TYPE
      ] = action.payload;
    },
  },
});

export const {
  setData: setOwnerFormData,
  setContactPersonType,
  setOwnerType,
  setUIField,
  setUIObjectField,
  setAvailableCities,
} = ownerSlice.actions;

export const { reducer } = ownerSlice;
